import { defineStore } from 'pinia'
import { useActivityStore } from './activity'
import { isIndexStatePending as isPending } from '~~/shared/items/itemPending'
import type { UploadedItem } from '~~/shared/items/uploadedItem'
import { isUuid } from '#shared/uuid'
import {
  stateFromStatus, type IndexJob, type IndexRow, type IndexSource, type IndexState, type UploadFailureKind,
} from '~/composables/documents-indexing/indexJob'
import { useIndexingUploads } from '~/composables/documents-indexing/useIndexingUploads'
import { indexingTask } from '~/composables/documents-indexing/indexingTask'
import { reportUsageLimit } from '~/composables/useUsageLimit'
import { viewerId } from '~/composables/useViewerId'
import { isOwnQuotaBlock } from '~/utils/quotaBlockMessage'
import { USAGE_LIMIT_REACHED } from '~~/shared/usage/usageLimit'

/**
 * Indexing state for documents this session is watching — https://app.notion.com/p/3c304e9a59288182a164c25dcd609277 §3.1.
 *
 * Indexing runs asynchronously across nine fire-and-forget entry points and used to be invisible:
 * the only aggregate signal was whether you happened to be looking at the folder containing the
 * row. This store is the single source of truth for "what is Marcus still chewing on", and the
 * single owner of the poller that answers it.
 *
 * It is deliberately session-scoped and in-memory: a reload forgets settled jobs. Durable history
 * would need a jobs table, which we explicitly did not build (§8).
 */

// The job types live beside the modules split out of this store (MAR-948); callers import them
// from here, as they always have.
export type { IndexJob, IndexRow, IndexSource, IndexState, IndexStage } from '~/composables/documents-indexing/indexJob'
export { stateFromStatus } from '~/composables/documents-indexing/indexJob'

/** Settled jobs kept for the session so the tray can say "18 listos hoy" without a history table. */
const MAX_SETTLED = 50

/** Client-minted job ids. A counter, not a uuid: it never leaves the tab. */
let localSeq = 0

export const useDocumentsIndexingStore = defineStore('documents-indexing', () => {
  const { apiFetch } = useApi()
  // Resolved FIRST (MAR-948). `upload()` reveals the panel and every failure path calls `t()`; both
  // used to be declared at the bottom of this setup, below functions that close over them — a
  // temporal-dead-zone read waiting for the first caller that ran during setup.
  const activity = useActivityStore()
  const { t } = useI18n()

  /** Insertion-ordered: the tray renders newest work last, oldest first. */
  const jobs = ref<IndexJob[]>([])

  /** Callbacks fired once per settling job, so surfaces can refetch their richer payloads. */
  const settleListeners = new Set<(job: IndexJob) => void>()

  const active = computed(() => jobs.value.filter(j => isPending(j.state)))
  // A quota block needs attention as much as a failure does — it is a decision for the user (raise the
  // limit, wait, upgrade), just not one a Retry can make for them.
  const needsAttention = computed(() => jobs.value.filter(j => j.state === 'failed' || j.state === 'quota_blocked' || j.state === 'awaiting_confirmation'))
  const settled = computed(() => jobs.value.filter(j => !isPending(j.state) && j.state !== 'failed' && j.state !== 'quota_blocked' && j.state !== 'awaiting_confirmation'))
  /**
   * Refused uploads (§5). They settle instantly, so without a row here the library path would look
   * like nothing happened at all — the user drops a file, no row appears, no explanation. They stay
   * until dismissed or the session ends.
   */
  const duplicates = computed(() => jobs.value.filter(j => j.state === 'duplicate'))

  /**
   * Finished successfully in this session. Kept visible on purpose: "it vanished" and "it worked"
   * look identical otherwise, and the moment a document becomes usable is worth confirming.
   */
  const ready = computed(() => jobs.value.filter(j => j.state === 'ready' && j.settledAt !== undefined))

  /** True while a document is still flagged as newly indexed — drives the "Nuevo" marker in lists. */
  function isRecentlyIndexed(itemId: string): boolean {
    return ready.value.some(j => j.itemId === itemId)
  }

  /** Anything worth showing a row for: in flight, waiting on the user, refused, or just finished. */
  const visible = computed(() => [...active.value, ...needsAttention.value, ...duplicates.value, ...ready.value])
  const hasWork = computed(() => visible.value.length > 0)

  function stateFor(itemId: string): IndexState | null {
    return jobs.value.find(j => j.itemId === itemId)?.state ?? null
  }

  function isItemPending(itemId: string): boolean {
    const s = stateFor(itemId)
    return s !== null && isPending(s)
  }

  /** A fresh client-side job for a file about to be uploaded (MAR-113) — see `IndexJob.id`. */
  function mintJob(input: { name: string; path: string; source: IndexSource }): IndexJob {
    return {
      id: `job-${++localSeq}`,
      itemId: null,
      name: input.name,
      fileName: input.name,
      path: input.path,
      source: input.source,
      state: 'uploading',
      errorMessage: null,
      startedAt: Date.now(),
    }
  }

  function fail(job: IndexJob, message: string, kind: UploadFailureKind = 'unknown') {
    job.state = 'failed'
    job.errorMessage = message
    job.failureKind = kind
    job.settledAt = Date.now()
  }

  /**
   * Register documents to watch. Called by every upload/ingest site the moment its POST resolves —
   * this is the only registration point the feature adds.
   *
   * Re-tracking a known id updates it in place rather than duplicating the row, so a chat upload of
   * something already visible in the library doesn't produce two entries.
   */
  const { upload, cancel } = useIndexingUploads({
    apiFetch, t, jobs, mintJob, pushJob, claim, fail, trim, ensurePoll, reveal: () => activity.reveal(),
  })

  function track(
    items: UploadedItem[],
    source: IndexSource = 'library',
  ) {
    for (const item of items) {
      // Optimistic client-side placeholders ("pending-…") have no server row to poll for.
      if (!item?.id || !isUuid(item.id)) continue

      const existing = jobs.value.find(j => j.itemId === item.id)
      const job = existing ?? pushJob(mintJob({
        name: item.name ?? item.file_name ?? 'Documento',
        path: item.path ?? '',
        source,
      }))
      claim(job, item)
    }
    trim()
    ensurePoll()
  }

  /**
   * Add a job and return the REACTIVE row, not the object we constructed.
   *
   * `jobs` is a reactive array, so a pushed object is stored as the raw target; mutating that
   * reference directly never triggers the projection and the panel would show the row appear and
   * then never change again.
   */
  function pushJob(job: IndexJob): IndexJob {
    jobs.value.push(job)
    return jobs.value[jobs.value.length - 1]!
  }

  /**
   * Bind a job to its server row — the ONE place an `itemId` is assigned.
   *
   * Two things can learn about the same item independently: the upload response, and the feed's
   * discovery half (which returns any recent working item of yours, tracked or not — that is how a
   * reload re-adopts work the session never saw). The server INSERTs the row before it responds, so
   * for one response round trip the row is publicly discoverable while the uploading client still
   * has `itemId: null` and cannot recognise it as its own.
   *
   * A tick landing in that gap used to mint a SECOND job for the same item. Both then claimed the
   * same id, `applyRows` matches with `find()` so only ever updated one, and the orphan span forever
   * on "Subiendo…" — unkillable, since `isPending` blocks dismissal and exempts it from `trim`.
   *
   * Keying jobs by `itemId` made that impossible for free; MAR-113 decoupled them to keep one row
   * across the upload→indexing handoff, so the invariant has to be enforced rather than assumed.
   * Whoever claims an id keeps it, and the loser is dropped: the uploading job wins because it holds
   * the true `startedAt` and the panel row the user is already looking at.
   */
  function claim(job: IndexJob, item: UploadedItem) {
    const impostor = jobs.value.find(j => j !== job && j.itemId === item.id)
    if (impostor) untrackJob(impostor.id)

    // `reindexed` = the bytes matched a FAILED document, so the upload retried it rather than
    // being refused. That is real work starting, not a duplicate to acknowledge.
    const isDuplicate = item.duplicate === true && item.reindexed !== true
    // A duplicate that is STILL INDEXING keeps its real state: the upload joined an in-flight run,
    // so the tray must keep tracking it instead of parking it as a finished duplicate (§5.3).
    const derived = stateFromStatus(item.status)
    const state: IndexState = isDuplicate && !isPending(derived) ? 'duplicate' : derived

    job.itemId = item.id
    job.state = state
    job.name = item.name ?? item.file_name ?? job.name
    job.fileName = item.file_name ?? job.fileName
    job.duplicate = isDuplicate || job.duplicate
    if (!isPending(state)) job.settledAt ??= Date.now()
  }

  function untrack(itemId: string) {
    jobs.value = jobs.value.filter(j => j.itemId !== itemId)
  }

  /** Remove one job by its own identity — the only handle an upload that never got an id has. */
  function untrackJob(id: string) {
    jobs.value = jobs.value.filter(j => j.id !== id)
  }

  /** Drop settled rows beyond the cap, oldest first. Pending work is never trimmed. */
  function trim() {
    const done = jobs.value.filter(j => !isPending(j.state))
    if (done.length <= MAX_SETTLED) return
    const drop = new Set(
      done.sort((a, b) => (a.settledAt ?? 0) - (b.settledAt ?? 0)).slice(0, done.length - MAX_SETTLED).map(j => j.id),
    )
    jobs.value = jobs.value.filter(j => !drop.has(j.id))
  }

  /** Subscribe to job completion. Returns an unsubscribe fn. */
  function onSettled(fn: (job: IndexJob) => void): () => void {
    settleListeners.add(fn)
    return () => settleListeners.delete(fn)
  }

  /**
   * Apply one tick of the shared activity feed (MAR-109).
   *
   * This store no longer owns a timer: `useActivityFeed` polls `POST /api/activity` once for every
   * kind and hands the indexing half here. The state machine, the quota parsing and the projection
   * all still live in this file — only the transport moved.
   *
   * `rows` carries two populations: every id this store is tracking (resolved explicitly, terminal
   * state included) and any other recent work of the user's that the server knows about, which is how
   * a reload rehydrates a job this session never tracked.
   */
  /**
   * A document the gateway refused → `useUsageLimit`, the path every other category takes (MAR-1009):
   * the red banner naming the plan, and the `usage` Activity item. Only for a block that names the
   * plan — one recorded before MAR-1009, or a run-ceiling stop, is not a plan limit to announce —
   * and only to the user whose seat hit it.
   */
  function reportQuotaBlock(row: IndexRow) {
    if (row.state !== 'quota_blocked' || !row.quota?.planLabel) return
    // Another user's seat hit it: not this user's limit, so not their banner (MAR-1046 AC17).
    if (!isOwnQuotaBlock(row.quota, viewerId())) return
    reportUsageLimit({
      code: USAGE_LIMIT_REACHED,
      category: row.quota.category,
      plan: row.quota.plan ?? null,
      planLabel: row.quota.planLabel,
      reason: row.quota.reason,
    })
  }

  function applyRows(rows: IndexRow[], missing: string[] = []) {
    for (const row of rows) {
      const job = jobs.value.find(j => j.itemId === row.id)

      // Not tracked by this session: adopt it, so a run still in flight (or one that stopped needing
      // a decision) survives a reload instead of quietly vanishing from the bell.
      if (!job) {
        if (row.state === 'ready') continue // a finished document is not activity
        jobs.value.push({
          id: `job-${++localSeq}`,
          itemId: row.id,
          name: row.name,
          fileName: row.fileName,
          path: '',
          source: 'library',
          state: row.state,
          errorMessage: row.errorMessage,
          quota: row.quota ?? null,
          stale: row.stale === true,
          stage: row.stage ?? null,
          startedAt: Date.now(),
          settledAt: isPending(row.state) ? undefined : Date.now(),
          dismissedAt: row.dismissedAt ?? null,
        })
        // A block found on adoption — above all after a reload — is still the user's live problem
        // until they acknowledge the row: the 10-02 dev re-test (MAR-1009) reloaded onto a refused
        // upload and saw neither the banner nor its Activity item. Adoption happens once per row per
        // session, and the feed only returns blocks from its 24 h window, so this is not a banner on
        // every tick or for last month's document.
        if (!row.dismissedAt) reportQuotaBlock(row)
        continue
      }

      // Carried on every tick, not just adoption: restoring a row has to clear it without a reload.
      job.dismissedAt = row.dismissedAt ?? null

      // `awaiting_confirmation` is neither pending nor settled: nothing is running, but the user can
      // still start the read from the document viewer, and when that read finishes the row comes back
      // `ready`. Treating it as terminal here is what left "lectura pendiente" on a document that had
      // already been read (#84) — the job never settled, so the `onSettled` refetch that refreshes the
      // browser, the groups and the cached row never fired, and only a reload cleared it.
      const awaitingUser = job.state === 'awaiting_confirmation'
      if (!isPending(job.state) && !awaitingUser) continue

      if (isPending(row.state)) {
        // Still working. Carry the phase and the staleness flag: the first tells the user WHAT is
        // taking time, the second offers a way out of a job whose worker died.
        // A confirmed read moves awaiting_confirmation → working, so adopt the new state too: the tray
        // must stop asking for a decision the user has already made.
        if (awaitingUser) job.state = row.state
        job.stale = row.stale === true
        job.stage = row.stage ?? null
        continue
      }

      // Same human decision still outstanding — not a transition, and emphatically not a settle.
      if (awaitingUser && row.state === 'awaiting_confirmation') continue

      job.state = row.state
      job.errorMessage = row.errorMessage
      job.quota = row.quota ?? null
      job.settledAt = Date.now()
      // A read the gateway refused, watched as it happened (MAR-1009): the red banner naming the plan,
      // beside this row's own amber "límite" badge. Only on the transition, so it fires once.
      reportQuotaBlock(row)
      for (const fn of settleListeners) fn(job)
    }

    // Tracked but absent → the document was deleted mid-flight. Stop watching a ghost.
    for (const id of missing) untrack(id)

    trim()
  }

  /**
   * Ask the shared feed for a tick.
   *
   * Kept under its original name because callers outside this store use it (`useBrowser.ts:227`), and
   * its meaning is unchanged from their side: "there is work now, start watching". The feed registers
   * itself via `onPollRequested`; before it mounts this is a no-op, and the feed's own initial fetch
   * covers anything tracked in the meantime.
   */
  function ensurePoll() {
    if (!import.meta.client) return
    requestPoll?.()
  }

  let requestPoll: (() => void) | null = null
  /** The shared feed registers here. One subscriber; a second would mean two timers again. */
  function onPollRequested(fn: () => void) {
    requestPoll = fn
  }

  /**
   * Ids the shared feed must keep asking about.
   *
   * An `uploading` job is pending but has no `itemId` — the server has not heard of it yet — so it is
   * deliberately absent here. Including it would make the feed ask about a row that cannot exist.
   */
  const pendingIds = computed(() => active.value.map(j => j.itemId).filter((id): id is string => !!id))

  /**
   * Ids the feed must RESOLVE — a wider set than the ids that keep it ticking.
   *
   * A job waiting on a human decision is not work in flight, so it must never arm the 3s timer: the
   * decision can take days. But while the timer runs for some OTHER reason — above all the read the
   * user just confirmed, which the runs half reports — the server has to be asked about this id
   * explicitly, because a finished read is `ready`, and `ready` is precisely what discovery drops.
   * Without it the terminal state never arrives and the job never settles (#84).
   */
  const trackedIds = computed(() => [...new Set([
    ...pendingIds.value,
    ...jobs.value
      .filter(j => j.state === 'awaiting_confirmation')
      .map(j => j.itemId)
      .filter((id): id is string => !!id),
  ])])

  /**
   * Work in one folder that exists ONLY on the client — how a folder view renders an upload without
   * holding any state of its own (MAR-113).
   *
   * The criterion is `itemId === null`, deliberately structural rather than a list of state names.
   * Anything with a server row already reaches a folder view through its own payload, so this
   * returns exactly the complement and the two can never disagree about the same file. A state
   * filter would need widening every time the state machine grew; this does not.
   */
  function pendingUploadsIn(path: string): IndexJob[] {
    return jobs.value.filter(j => j.path === path && j.itemId === null && isPending(j.state))
  }

  function clearSettled() {
    jobs.value = jobs.value.filter(j => isPending(j.state) || j.state === 'awaiting_confirmation' || j.state === 'failed' || j.state === 'quota_blocked')
  }

  /**
   * Restart a job — for a document stranded at `processing` (dead worker) or one that failed.
   * Re-runs the pipeline from the STORED file, so it needs no re-upload.
   */
  async function retry(itemId: string) {
    const job = jobs.value.find(j => j.itemId === itemId)
    if (!job) return
    job.state = 'working'
    job.stale = false
    job.errorMessage = null
    job.failureKind = undefined
    job.settledAt = undefined
    try {
      await apiFetch(`/api/items/${itemId}/reprocess`, { method: 'POST' })
    } catch {
      // Tracked by useApi. The poller will report the real state on the next tick either way.
    }
    ensurePoll()
  }

  /**
   * Dismiss one settled row (the failed/duplicate acknowledgement).
   *
   * The acknowledgement is PERSISTED (MAR-109). Indexing state is durable, so an in-memory dismissal
   * was re-adopted from `items.index_state` on the next feed tick — three seconds later — which made
   * the button look broken whenever anything else was still running. `markDocumentProcessing` clears
   * the flag again, so a document that fails a second time comes back.
   */
  function dismissJob(id: string) {
    const job = jobs.value.find(j => j.id === id)
    if (!job || isPending(job.state)) return
    untrackJob(id)
    // A `duplicate` row is client-side only — there is no server state saying "refused", so there is
    // nothing to acknowledge and the POST would 404-by-no-op. A refused upload has no server row at
    // all, for the same reason.
    if (job.state !== 'duplicate' && job.itemId) {
      apiFetch('/api/activity/dismiss', { method: 'POST', body: { kind: 'indexing', id: job.itemId } })
        .catch(() => { /* Tracked by useApi. Worst case the row returns on the next tick. */ })
    }
  }

  /** Dismiss by server id — for callers that only hold an `itemId`. */
  function dismiss(itemId: string) {
    const job = jobs.value.find(j => j.itemId === itemId)
    if (job) dismissJob(job.id)
  }

  /** Undo a dismissal, from the "mostrar descartados" list. */
  function restore(itemId: string) {
    const job = jobs.value.find(j => j.itemId === itemId)
    if (job) job.dismissedAt = null
    apiFetch('/api/activity/dismiss', { method: 'POST', body: { kind: 'indexing', id: itemId, undo: true } })
      .catch(() => { /* Tracked by useApi. The next tick reports the real state. */ })
  }

  /** Drop acknowledged rows when the toggle goes off — they are absent from the feed from then on. */
  function purgeDismissed() {
    jobs.value = jobs.value.filter(j => !j.dismissedAt)
  }

  // ── Activity Center producer (https://app.notion.com/p/3c304e9a59288185892ef46b9483144f §4) ─────────────────────────────
  //
  // This store stays the owner of indexing: the poller, the state machine and the quota payload all
  // live here. It only PUBLISHES a normalized view (`indexingTask`, a pure projection), so the panel
  // can render indexing without knowing anything about it.

  /**
   * Publish on every change. A watcher is the wrong tool for most things in this codebase, but here
   * the source is a reactive list mutated from a timer callback and the sink is another store — there
   * is no user gesture to hang it off. Kept to a single pure projection with no side effects.
   */
  watch(jobs, () => {
    activity.replaceKind(
      'indexing',
      visible.value.map(job => indexingTask(job, t, viewerId())),
      Object.fromEntries(visible.value.flatMap(job => [
        [`indexing:${job.id}:retry`, () => job.itemId && retry(job.itemId)],
        [`indexing:${job.id}:cancel`, () => cancel(job.id)],
        [`indexing:${job.id}:dismiss`, () => dismissJob(job.id)],
        [`indexing:${job.id}:restore`, () => job.itemId && restore(job.itemId)],
      ])),
    )
  }, { deep: true, immediate: true })

  return {
    jobs,
    active,
    needsAttention,
    duplicates,
    ready,
    isRecentlyIndexed,
    settled,
    visible,
    hasWork,
    stateFor,
    isItemPending,
    pendingUploadsIn,
    upload,
    cancel,
    track,
    untrack,
    onSettled,
    ensurePoll,
    onPollRequested,
    applyRows,
    pendingIds,
    trackedIds,
    retry,
    clearSettled,
    dismiss,
    restore,
    purgeDismissed,
  }
})
